import { useCallback, useEffect, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { CheckIcon, PencilIcon, WandSparklesIcon } from "lucide-react";

import { Button } from "simplydo/react/ui/button";
import { Card, CardContent, CardHeader } from "simplydo/react/ui/card";
import { Divider } from "simplydo/react/ui/divider";
import { FormField } from "simplydo/react/ui/form";
import { H4, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

import actions from "actions";
import api from "api";
import { useAppDispatch } from "store";
import { useUserReload } from "utils/useUserReload";
import util from "utils/utils";

type INewChallengeModalWrapper = {
  newChallengeOpen: boolean;
  onCloseModal: () => void;
};

const NewModal = ({
  newChallengeOpen,
  onCloseModal,
}: INewChallengeModalWrapper) => {
  const [creatingChallengeType, setCreatingChallengeType] = useState<
    "" | "manual"
  >("");
  const [name, setName] = useState<string>("");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const reloadUser = useUserReload();

  const createChallenge = useCallback(() => {
    setIsLoading(true);
    api.challenges.create(
      name,
      ({ _id }) => {
        setIsLoading(false);
        reloadUser(() => {
          dispatch(actions.user.onFollow("challenge", _id));
          navigate(`/challenges/${_id}/settings`);
        });
      },
      (err) => {
        toast.error(err.message);
        setIsLoading(false);
      },
    );
  }, [name, navigate, dispatch, reloadUser]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent | React.KeyboardEvent<HTMLInputElement>) => {
      if (e.keyCode === 13 && newChallengeOpen && name !== "") {
        createChallenge();
        e.stopPropagation();
        e.preventDefault();
        return true;
      }
    },
    [createChallenge, name, newChallengeOpen],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown, false);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  const handleCloseModal = useCallback(() => {
    setCreatingChallengeType("");
    setName("");
    onCloseModal();
  }, [onCloseModal]);

  return (
    <Modal
      open={newChallengeOpen}
      onOpen={() => setCreatingChallengeType("")}
      onClose={handleCloseModal}
    >
      <ModalHeader>Create {t("generic.challenge")}</ModalHeader>
      {!creatingChallengeType ? (
        <ModalContent>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-evenly",
            }}
          >
            <Card
              fluid
              onClick={() => navigate("/challenges/new/genai")}
              style={{ margin: "0 20px 0 0" }}
              elevation="small"
            >
              <CardHeader>
                <H4>
                  <Icon icon={WandSparklesIcon} />
                  Guided {t("generic.challenge")} build
                </H4>
              </CardHeader>
              <CardContent>
                {util.appName()} will help you through the build process,
                getting you to a start point you can work from.
              </CardContent>
            </Card>
            <Divider vertical>Or</Divider>
            <Card
              fluid
              onClick={() => setCreatingChallengeType("manual")}
              style={{ margin: "0 0 0 20px" }}
              elevation="small"
            >
              <CardHeader>
                <H4>
                  <Icon icon={PencilIcon} />
                  Start from scratch
                </H4>
              </CardHeader>
              <CardContent>
                Start building from a blank {t("generic.challenge")}.
              </CardContent>
            </Card>
          </div>
        </ModalContent>
      ) : creatingChallengeType === "manual" ? (
        <ModalContent>
          <p>
            What would you like to call your {t("generic.challenge")}? You can
            always change this later.
          </p>
          <Input
            fluid
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
              handleKeyDown(e)
            }
            placeholder={`${t("common:capitalise", { key: "generic.challenge" })} name...`}
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setName(e.target.value)
            }
          />
        </ModalContent>
      ) : null}

      <ModalActions>
        <Button
          onClick={() => {
            handleCloseModal();
          }}
        >
          {t("generic.cancel")}
        </Button>
        {creatingChallengeType === "manual" && (
          <Button
            onClick={() => createChallenge()}
            loading={isLoading}
            disabled={isLoading || name.trim() === ""}
            color="primary"
          >
            <Icon icon={CheckIcon} />
            {`Create ${t("generic.challenge")}`}
          </Button>
        )}
      </ModalActions>
    </Modal>
  );
};

export default NewModal;
